import { Box, Typography, Alert } from '@mui/material'
import { useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import { DialogBox } from '~pages/pages-behind-login/chartering/pre-fixture-page/components/dialog-box'
import { UnsavedChangesCloseDialog } from '~pages/pages-behind-login/chartering/call-log-page/components/unsaved-changes-close-dialog'
import {
  usePostPortInfo,
  type PostPortInfoPayload,
} from '~hooks/queries/port-info/use-post-port-info'
import { usePatchPortInfo } from '~hooks/queries/port-info/use-patch-port-info'
import { useDeletePortInfo } from '~hooks/queries/port-info/use-delete-port-info'
import { useGetPortInfoById } from '~hooks/queries/port-info/use-get-port-info'
import type { PortInfoFormData } from '../../types'
import { FooterButtons } from './sub-components/footer-buttons'
import { StepPagination } from '~components/molecules/step-pagination'
import { PageOne } from './sub-components/page-one/page-one'
import { PageTwo } from './sub-components/page-two/page-two'
import { PageTwoSimple } from './sub-components/page-two/page-two-simple'
import {
  buildPatchPayload,
  hasUnsavedChanges,
  getMissingRequiredFields,
} from '../../utils/form-utils'
import { COLORS } from '~theme/colors'

export const pageNumbers = ['1', '2'] as const
export type ContentPageNumber = (typeof pageNumbers)[number]

export const inputWidth = { xs: '100%', md: '300px' }
const modalHeightPage1 = '400px'
const modalHeightPage2Port = '80vh'
const modalHeightPage2Simple = '80vh'

const page2SlotProps = {
  paper: { sx: { width: '90vw', maxWidth: '2000px' } },
}

function isPageOneValid(formData: PortInfoFormData): boolean {
  return Boolean(
    formData.type && formData.vesselName && formData.imoNumber && formData.pool,
  )
}

function isPageTwoValidPort(formData: PortInfoFormData): boolean {
  return Boolean(
    formData.port?.port &&
    formData.terminalName &&
    formData.berthName &&
    formData.dateOfArrival &&
    formData.dateOfDeparture &&
    formData.operation &&
    formData.cargoType &&
    formData.arrivalDraft !== undefined &&
    formData.departureDraft !== undefined &&
    formData.ispsSecurityLevel &&
    formData.cargoConnections &&
    formData.manifoldPressure !== undefined,
  )
}

function isPageTwoValidSimple(formData: PortInfoFormData): boolean {
  if (formData.type === 'continent') {
    return Boolean(formData.continent?.continent)
  }
  if (formData.type === 'country') {
    return Boolean(formData.country?.country)
  }
  return false
}

function getPageNumbers(): ContentPageNumber[] {
  return ['1', '2']
}

export function AddVisitFlow({
  onClose,
}: {
  onClose: (hasError?: boolean) => void
}) {
  const [formData, setFormData] = useState<PortInfoFormData>({})
  const [modalContentPage, setModalContentPage] =
    useState<ContentPageNumber>('1')
  const [showUnsavedCloseConfirm, setShowUnsavedCloseConfirm] = useState(false)
  const [portInfoId, setPortInfoId] = useState<string | null>(null)
  const [missingFields, setMissingFields] = useState<
    (keyof PortInfoFormData)[]
  >([])
  const { setStatus } = useAnimatedAlert()

  const {
    mutate: postVisit,
    isPending: isPendingPostVisit,
    isError: isErrorPostVisit,
  } = usePostPortInfo()

  const {
    mutate: patchVisit,
    isPending: isPendingPatchVisit,
    isError: isErrorPatchVisit,
  } = usePatchPortInfo()

  const { mutate: deleteVisit } = useDeletePortInfo()

  const { data: portInfoData } = useGetPortInfoById(portInfoId)

  const currentPageNumbers = getPageNumbers()
  const isPortType = formData.type === 'port'

  const arePagesValid = (() => {
    if (modalContentPage === '1') {
      return isPageOneValid(formData)
    }
    if (isPortType) {
      return isPageTwoValidPort(formData)
    }
    return isPageTwoValidSimple(formData)
  })()

  function requestClose() {
    if (hasUnsavedChanges(formData) && !showUnsavedCloseConfirm) {
      setShowUnsavedCloseConfirm(true)
      return
    }
    onClose()
  }

  function onNext() {
    if (portInfoId) {
      setModalContentPage('2')
      return
    }

    if (!formData.type) {
      setStatus('Please select a type', undefined, 'error')
      return
    }

    const payload: PostPortInfoPayload = {
      type: formData.type,
      vesselName: formData.vesselName || '',
      imoNumber: formData.imoNumber || '',
      pool: formData.pool || '',
    }

    postVisit(payload, {
      onSuccess: (response) => {
        setPortInfoId(response.insertedId)
        setModalContentPage('2')
      },
      onError: () => {
        setStatus('Failed to create entry', undefined, 'error')
      },
    })
  }

  function onSave() {
    if (!portInfoId) {
      setStatus('Missing port info ID', undefined, 'error')
      return
    }

    const pageKey = isPortType ? '2' : '2-simple'
    const missing = getMissingRequiredFields(formData, pageKey as '2')
    if (missing.length > 0) {
      setMissingFields(missing)
      setStatus('Please fill in all required fields', undefined, 'error')
      return
    }

    setMissingFields([])

    const payload = buildPatchPayload(formData)

    patchVisit(
      { portInfoId, payload },
      {
        onSuccess: () => {
          setStatus('Entry added successfully', undefined, 'success')
          onClose()
        },
        onError: () => {
          setStatus('Failed to save entry', undefined, 'error')
        },
      },
    )
  }

  const isLoading = isPendingPostVisit || isPendingPatchVisit
  const hasError = isErrorPostVisit || isErrorPatchVisit

  function renderContent() {
    if (isLoading) {
      return <CircularProgress aria-label="Loading…" />
    }

    if (hasError) {
      return <Alert severity="error">There was an error saving the entry</Alert>
    }

    return (
      <>
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Typography variant="h3">Add Entry</Typography>
            {currentPageNumbers.length > 1 && (
              <StepPagination
                active={modalContentPage}
                pages={currentPageNumbers}
              />
            )}
          </Box>
          {modalContentPage === '1' && (
            <PageOne formData={formData} updateFormData={setFormData} />
          )}
          {modalContentPage === '2' && isPortType && (
            <PageTwo
              formData={formData}
              updateFormData={setFormData}
              portInfoId={portInfoId || undefined}
              files={portInfoData?.files}
              missingFields={missingFields}
            />
          )}
          {modalContentPage === '2' && !isPortType && (
            <PageTwoSimple
              formData={formData}
              updateFormData={setFormData}
              portInfoId={portInfoId || undefined}
              files={portInfoData?.files}
              missingFields={missingFields}
            />
          )}
        </Box>
        {modalContentPage === '2' && missingFields.length > 0 && (
          <Typography sx={{ color: COLORS.red.mid, textAlign: 'right', mb: 1 }}>
            Some required fields are not filled
          </Typography>
        )}
        <FooterButtons
          onClose={requestClose}
          onSave={onSave}
          onNext={onNext}
          onPrevious={() => setModalContentPage('1')}
          active={modalContentPage}
          arePagesValid={arePagesValid}
          pages={currentPageNumbers}
        />
      </>
    )
  }

  return (
    <>
      <DialogBox
        open
        onClose={requestClose}
        {...(modalContentPage === '2' ? { slotProps: page2SlotProps } : {})}
      >
        <Box
          sx={{
            height:
              modalContentPage === '1'
                ? modalHeightPage1
                : isPortType
                  ? modalHeightPage2Port
                  : modalHeightPage2Simple,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {renderContent()}
        </Box>
      </DialogBox>
      <UnsavedChangesCloseDialog
        open={showUnsavedCloseConfirm}
        onCancel={() => setShowUnsavedCloseConfirm(false)}
        onConfirmClose={() => {
          setShowUnsavedCloseConfirm(false)
          if (portInfoId) {
            deleteVisit(portInfoId, {
              onError: () => {
                setStatus('Failed to delete draft entry', undefined, 'error')
              },
            })
          }
          onClose()
        }}
      />
    </>
  )
}
